// Вставка диапазона из буфера обмена.
//
// Правило 6 требует показать «ляжет N значений, отклонено M и почему» ДО применения, а не
// после. Причина не в вежливости: табельщик копирует кусок из своего Excel, и если половина
// значений молча не ляжет, он узнает об этом на сверке в конце месяца — когда людям уже
// начислили.
//
// Разбор идёт по знаку табуляции и переводу строки: ровно так Excel и кладёт диапазон в буфер.

import type { CellState, GridColumn } from './types';

export interface PastePlan<TRow> {
  /** Что ляжет: строка, колонка и новое значение. */
  readonly accepted: readonly { row: TRow; columnId: string; value: string }[];
  /** Что отклонено и ПОЧЕМУ. Пусто — всё легло. */
  readonly rejected: readonly { reason: string; count: number }[];
  /** Одна фраза для человека. */
  readonly summary: string;
}

/** Разобрать текст из буфера в прямоугольник значений. */
export function parseClipboard(text: string): string[][] {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n+$/, '')
    .split('\n')
    .map((строка) => строка.split('\t'));
}

/**
 * Разложить вставку по сетке от места каретки и сказать, что ляжет, а что нет.
 *
 * Ничего не применяет: решение принимает человек, увидев итог.
 */
export function planPaste<TRow>({
  clipboard,
  matrix,
  rows,
  columns,
  rowIndex,
  columnIndex,
  validate,
}: {
  /** Текст из буфера. Не задан — раскладку берут из `matrix`. */
  clipboard?: string;
  /**
   * Готовый прямоугольник значений вместо текста буфера.
   *
   * Нужен автозаполнению вниз: собирать для него строку с табуляциями и переводами строк, чтобы
   * тут же её разобрать, — это лишний круг, на котором ПУСТЫЕ значения теряются: разбор
   * буфера срезает хвостовые переводы строк (так кладёт Excel), и «стереть колонку вниз»
   * превращалось в «стереть одну ячейку».
   */
  matrix?: readonly (readonly string[])[];
  rows: readonly TRow[];
  columns: readonly GridColumn<TRow>[];
  rowIndex: number;
  columnIndex: number;
  /**
   * Проверка значения. Вернуть причину отказа либо null.
   *
   * Колонка приходит третьим доводом, потому что в одной сетке колонки проверяются по-разному:
   * в официальном контуре рядом стоят сумма в рублях, процент удержания и свободная заметка, и
   * одна проверка на всех либо пропустит мусор в деньги, либо забракует законный текст.
   */
  validate?: (value: string, cell: CellState, columnId: string) => string | null;
}): PastePlan<TRow> {
  const прямоугольник = matrix ?? parseClipboard(clipboard ?? '');
  const принято: { row: TRow; columnId: string; value: string }[] = [];
  const отказы = new Map<string, number>();

  const отклонить = (причина: string) => отказы.set(причина, (отказы.get(причина) ?? 0) + 1);

  прямоугольник.forEach((строкаБуфера, сдвигСтроки) => {
    const строка = rows[rowIndex + сдвигСтроки];
    if (строка === undefined) {
      строкаБуфера.forEach(() => отклонить('строк в буфере больше, чем строк в таблице'));
      return;
    }
    строкаБуфера.forEach((значение, сдвигКолонки) => {
      const колонка = columns[columnIndex + сдвигКолонки];
      if (колонка === undefined) {
        отклонить('колонок в буфере больше, чем колонок в таблице');
        return;
      }
      const ячейка = колонка.cell(строка);
      if (ячейка.behaviour !== 'input') {
        отклонить(
          ячейка.reason ??
            (ячейка.behaviour === 'blocked'
              ? 'править нельзя'
              : 'колонка считается, а не вводится'),
        );
        return;
      }
      const текст = значение.trim();
      const отказ = validate?.(текст, ячейка, колонка.id) ?? null;
      if (отказ !== null) {
        отклонить(отказ);
        return;
      }
      принято.push({ row: строка, columnId: колонка.id, value: текст });
    });
  });

  const отклонено = [...отказы.entries()].map(([reason, count]) => ({ reason, count }));
  const всегоОтклонено = отклонено.reduce((итог, пункт) => итог + пункт.count, 0);
  const хвост =
    всегоОтклонено === 0
      ? ''
      : `, отклонено ${всегоОтклонено}: ${отклонено
          .map((пункт) => `${пункт.reason} (${пункт.count})`)
          .join('; ')}`;

  return {
    accepted: принято,
    rejected: отклонено,
    summary: `Ляжет ${принято.length} ${значений(принято.length)}${хвост}.`,
  };
}

function значений(количество: number): string {
  const сотня = количество % 100;
  const десяток = количество % 10;
  if (сотня >= 11 && сотня <= 14) {
    return 'значений';
  }
  if (десяток === 1) {
    return 'значение';
  }
  if (десяток >= 2 && десяток <= 4) {
    return 'значения';
  }
  return 'значений';
}
